import { useActionState, useOptimistic, useState } from 'react'
import { useFormStatus } from 'react-dom'
import { useLoaderData, useRevalidator } from 'react-router'

import { addPasskey, deletePasskey, renamePasskey } from '#entities/passkey'
import type { Passkey, RenamePasskeyErrorCode } from '#entities/passkey'
import { logout, updateEmail } from '#entities/session'
import type { UpdateEmailErrorCode } from '#entities/session'
import { ok } from '#shared/api/client'
import type { Result } from '#shared/api/client'
import { Alert, Icon, Screen, Section, Spinner } from '#shared/ui'
import { DashboardHeader } from './DashboardHeader'
import { EmailSection } from './EmailSection'
import { GuestUpgradeCard } from './GuestUpgradeCard'
import type { DashboardData } from './loadDashboard'
import { PasskeyNudge } from './PasskeyNudge'
import { PasskeyRow } from './PasskeyRow'
import type { DeleteFailure } from './PasskeyRow'
import { toAddPasskeyFailure, toThrownAddPasskeyFailure } from './addPasskeyFailure'
import type { AddPasskeyFailure } from './addPasskeyFailure'

/** What the alert under the header says when sign-out did not go through; never the raw message. */
const signOutFailure = {
  title: 'Не получилось выйти',
  text: 'Попробуйте ещё раз через минуту.',
}

/** A rename or a delete the server has not confirmed yet. */
type Change = { renamed: Pick<Passkey, 'id' | 'name'> } | { deleted: Passkey['id'] }

/** The list with a change the server has not confirmed yet applied to it. */
const withChange = (passkeys: Passkey[], change: Change) =>
  'deleted' in change
    ? passkeys.filter(passkey => passkey.id !== change.deleted)
    : passkeys.map(passkey => (passkey.id === change.renamed.id ? { ...passkey, name: change.renamed.name } : passkey))

/**
 * A guest sees what it is and the one way to an account, nothing else: no passkeys, no email, no sign-out. Hiding
 * them is presentation only; CAS refuses those requests under the guest's session (docs/adr/0003-guest-in-the-app.md).
 */
export const DashboardPage = () => {
  const data = useLoaderData<DashboardData>()
  if (data.me.accountType === 'guest') {
    return (
      <Screen align="top">
        <DashboardHeader label="Гостевой аккаунт" name={data.me.displayName} />
        <GuestUpgradeCard />
      </Screen>
    )
  }
  return <FullAccountDashboard {...data} />
}

/** The account by name, its passkeys and the ways to add and manage them, its email, and the way out. */
const FullAccountDashboard = ({ me, passkeys }: DashboardData) => {
  const revalidator = useRevalidator()
  // React shows the changed list while the row's action runs and goes back to the loader's once it settles.
  const [shownPasskeys, showChange] = useOptimistic(passkeys, withChange)
  // The same for the email: the address on its way to the server, until the loader has what it stored.
  const [shownEmail, showEmail] = useOptimistic(me.email)
  // Each row that could not be deleted explains why once it is back in the list. Keyed by passkey: with three or
  // more passkeys two deletes can be in flight at once, and one failure must not take the other's words away.
  const [deleteFailures, setDeleteFailures] = useState<Record<string, DeleteFailure>>({})
  const setDeleteFailure = (id: string, reason: DeleteFailure | null) =>
    setDeleteFailures(failures => {
      const next = { ...failures }
      if (reason) {
        next[id] = reason
      } else {
        delete next[id]
      }
      return next
    })

  const [failure, signOut] = useActionState(async (): Promise<typeof signOutFailure | null> => {
    try {
      if (!(await logout()).ok) {
        return signOutFailure
      }
    } catch {
      return signOutFailure
    }
    // The cookie is gone: the loader finds no session and redirects to sign-in.
    await revalidator.revalidate()
    return null
  }, null)

  // One action behind both the nudge's button and "Добавить" in the title row: a browser runs one ceremony at a
  // time, so while it is pending both controls wait.
  const [addFailure, add, adding] = useActionState(async (): Promise<AddPasskeyFailure | null> => {
    let result: Awaited<ReturnType<typeof addPasskey>>
    try {
      result = await addPasskey()
    } catch (error) {
      return toThrownAddPasskeyFailure(error)
    }
    if (!result.ok) {
      if (result.error.code === 'unauthenticated') {
        // The session ended under the page: the loader finds none and redirects to sign-in.
        await revalidator.revalidate()
        return null
      }
      return toAddPasskeyFailure(result.error)
    }
    // The list is about to change under every row, so what a row said about its last delete is stale: in particular
    // "the only passkey" after a `last_passkey` refusal, which the new passkey has just made untrue.
    setDeleteFailures({})
    // The action stays pending until the loader lists the new passkey, so the nudge goes and the first row's delete
    // comes on in one step.
    await revalidator.revalidate()
    return null
  }, null)

  /** A failure other than `passkey_not_found` goes back to the row, which explains it; a throw rejects to it. */
  const rename = async (id: string, name: string): Promise<Result<unknown, RenamePasskeyErrorCode>> => {
    showChange({ renamed: { id, name } })
    const result = await renamePasskey(id, name)
    // Deleted in another tab: the list is stale, not the name. The reload below takes the row away.
    if (!result.ok && result.error.code !== 'passkey_not_found') {
      return result
    }
    // The action stays pending until the loader has the new name, so the optimistic one never flickers back.
    await revalidator.revalidate()
    return ok(undefined)
  }

  const remove = async (id: string) => {
    setDeleteFailure(id, null)
    showChange({ deleted: id })
    let result: Awaited<ReturnType<typeof deletePasskey>>
    try {
      result = await deletePasskey(id)
    } catch {
      // The list is fine; the row comes back with the failed action and says so.
      setDeleteFailure(id, 'failed')
      return
    }
    if (!result.ok) {
      if (result.error.code === 'last_passkey') {
        // Lost a race with another tab: this is the only passkey now. The reload below turns its delete off with the same words.
        setDeleteFailure(id, 'lastPasskey')
        await revalidator.revalidate()
        return
      }
      // Deleted in another tab: the list is stale, and the reload below takes the row away all the same.
      if (result.error.code !== 'passkey_not_found') {
        setDeleteFailure(id, 'failed')
        return
      }
    }
    // The action stays pending until the loader no longer has the row, so it never flickers back.
    await revalidator.revalidate()
  }

  /** A failure other than a lost session goes back to the section, which explains it; a throw rejects to it. */
  const saveEmail = async (email: string | null): Promise<Result<unknown, UpdateEmailErrorCode>> => {
    showEmail(email)
    const result = await updateEmail(email)
    if (!result.ok) {
      if (result.error.code !== 'unauthenticated') {
        return result
      }
      // The session ended under the page: the loader finds none and redirects to sign-in. Nothing for the form to say.
      await revalidator.revalidate()
      return ok(undefined)
    }
    // The server stores the address its own way (the domain lower-cased) and answers with nothing, so the action
    // stays pending until the loader has read it back: what the row shows is what survives a reload.
    await revalidator.revalidate()
    return result
  }

  return (
    <Screen align="top">
      <DashboardHeader
        label="Аккаунт"
        name={me.displayName}
        action={
          <form action={signOut}>
            <SignOutButton />
          </form>
        }
      />
      {failure && <Alert title={failure.title}>{failure.text}</Alert>}
      {shownPasskeys.length === 1 && <PasskeyNudge action={add} pending={adding} />}
      {addFailure && <Alert title={addFailure.title}>{addFailure.text}</Alert>}
      <Section
        title="Пасскеи"
        action={
          <form action={add}>
            <AddPasskeyButton pending={adding} />
          </form>
        }
      >
        <ul>
          {shownPasskeys.map(passkey => (
            <PasskeyRow
              key={passkey.id}
              passkey={passkey}
              deletable={shownPasskeys.length > 1}
              deleteFailure={deleteFailures[passkey.id] ?? null}
              onRename={name => rename(passkey.id, name)}
              onDelete={() => remove(passkey.id)}
            />
          ))}
        </ul>
      </Section>
      <EmailSection email={shownEmail} onSave={saveEmail} />
    </Screen>
  )
}

const inlineButton =
  'flex h-11 shrink-0 items-center gap-1.5 text-sm font-bold text-ink-muted outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:text-ink-hint'

/** Its own component: `useFormStatus` reads the form it is rendered in. */
const SignOutButton = () => {
  const { pending } = useFormStatus()
  return (
    <button type="submit" disabled={pending} className={inlineButton}>
      <Icon name="logout" size={18} />
      Выйти
    </button>
  )
}

/** "Добавить" in the passkeys title row. Waits on the page's word, not its own form's: the nudge starts the same ceremony. */
const AddPasskeyButton = ({ pending }: { pending: boolean }) => (
  <button type="submit" disabled={pending} aria-busy={pending || undefined} className={inlineButton}>
    {pending ? <Spinner /> : <Icon name="plus" size={18} />}
    Добавить
  </button>
)
